import React from 'react';
import { Link } from 'react-router-dom';
import LegalShell, { Section, P, UL } from '../components/LegalShell';
import SEO, { webPageSchema } from '../components/SEO';
import { BRAND_NAME } from '../utils/brand';

// /medical-disclaimer — SEO landing page for the "medical disclaimer mydrai"
// keyword. Copy tracks the Website Build Handoff V1.0 §9 clinical guardrails
// and the doc-mandated H1 "Medical Disclaimer & Clinical Practice Standards".
export default function MedicalDisclaimer() {
  return (
    <>
      <SEO jsonLd={webPageSchema(
        '/medical-disclaimer',
        'Medical Disclaimer & Clinical Advisory Policy | MyDr.Ai',
        'MyDr.Ai medical disclaimer and clinical practice standards. Read our advisory policy on AI-assisted, doctor-led care in Malaysia.'
      )} />

      <LegalShell
        eyebrow="Clinical Advisory"
        title="Medical Disclaimer & Clinical Practice Standards"
        intro={`How ${BRAND_NAME} operates as a healthcare technology platform in Malaysia — with licensed doctors making every clinical decision.`}
        version="1.0"
        lastUpdated="18 August 2026"
        governingLaw="Laws of Malaysia"
      >
        <Section id="scope" number="1" heading="Scope & purpose of this disclaimer">
          <P>
            {BRAND_NAME} (operated by Melonco Sdn. Bhd.) is a healthcare
            technology platform. The information presented on this website,
            in-app content and any AI-assisted feature is for general education
            and workflow support. It does not constitute a medical diagnosis,
            treatment recommendation or a substitute for a face-to-face
            consultation with a licensed medical doctor in Malaysia.
          </P>
        </Section>

        <Section id="doctor-led" number="2" heading="Doctor-led clinical decisions">
          <P>
            All clinical decisions concerning your care — eligibility for a
            programme, prescription of any medicine, dose adjustment, follow-up
            frequency and discharge — are made by a Malaysia Medical Council
            (MMC) licensed doctor with an active Annual Practising Certificate
            (APC) at one of our panel clinics, after an in-person consultation.
            AI-assisted features never replace this doctor review.
          </P>
        </Section>

        <Section id="ai-role" number="3" heading="Role of AI on this platform">
          <UL>
            <li>Structuring the eligibility questionnaire and highlighting risk factors.</li>
            <li>Summarising your responses for the reviewing doctor.</li>
            <li>Scheduling reminders and generating educational content.</li>
            <li>Never issuing prescriptions, dosing instructions, or medical diagnoses.</li>
          </UL>
        </Section>

        <Section id="not-emergency" number="4" heading="Not for emergencies">
          <P>
            If you are experiencing a medical emergency (chest pain, severe
            allergic reaction, difficulty breathing, suicidal thoughts, or any
            life-threatening symptom), call 999 immediately or go to your
            nearest hospital emergency department. Do not use this website or
            any chat feature to seek emergency care.
          </P>
        </Section>

        <Section id="in-person" number="5" heading="In-person consultations only">
          <P>
            Programme consultations are conducted face-to-face at a licensed
            panel clinic. This is a regulatory requirement in Malaysia for the
            classes of medication used in medical weight management. Telehealth
            follow-ups (where permitted) do not replace the initial in-person
            visit.
          </P>
        </Section>

        <Section id="results" number="6" heading="Individual results vary">
          <P>
            Any results, testimonials or figures shown on this website reflect
            the experience of specific individuals. Your outcome depends on
            factors including your clinical suitability, adherence to the
            programme, existing health conditions and lifestyle. No guarantee
            of a particular weight-loss outcome is made or implied.
          </P>
        </Section>

        <Section id="contact" number="7" heading="Contact & feedback">
          <P>
            For clinical questions after a consultation, contact the panel
            clinic that reviewed your case. For platform issues or feedback,
            reach us via our{' '}
            <Link to="/contact" className="text-brand-700 underline">Contact page</Link>.
            See also our{' '}
            <Link to="/terms" className="text-brand-700 underline">Terms of Use</Link>{' '}
            and{' '}
            <Link to="/privacy" className="text-brand-700 underline">Privacy Policy</Link>.
          </P>
        </Section>
      </LegalShell>
    </>
  );
}
